import * as z from 'zod';

/**
 * Whose platform this is — the chrome outside any tenant.
 *
 * ## What this is NOT
 *
 * It is not tenant branding. `tenant_config.client_id` already resolves a
 * `ClientPreset` whose `brand` themes a team's own console, and that is per
 * account, read from the database, and changes between two tabs. This is the
 * mark on the sign-in page, the operator console and the app shell — surfaces
 * that exist before any tenant does, and where the question is not "whose
 * account am I in" but "whose product is this".
 *
 * Two are different questions and were about to become one variable. A demo of
 * e&'s platform shows e&'s mark on the sign-in page AND a clinic's own colours
 * once inside it; collapsing them would make the second impossible.
 *
 * ## Why an id and not a URL
 *
 * `NEXT_PUBLIC_PLATFORM_LOGO=/images/whatever.svg` would be fewer moving parts
 * and wrong: the mark needs an accessible NAME as much as a file, and the two
 * must not drift — a build serving e&'s wordmark while every screen reader says
 * "Unicity Labs" is worse than either alone, and nothing would catch it. An id
 * selects a pair that was written together.
 *
 * It also keeps the set closed. An unknown id is refused at startup rather than
 * rendering an empty box, which on the sign-in page is the whole header.
 */
export type PlatformBrand = {
  id: string;
  /**
   * The accessible name, and the alt text. Not translated: a wordmark is a
   * proper noun, and "إي آند" is not what the logo says.
   */
  label: string;
  /**
   * The wordmark file. `null` where no asset has been supplied yet — see `text`.
   */
  wordmark: string | null;
  /**
   * How the asset is coloured, which decides how it is DRAWN.
   *
   * `currentColor` masks the shape and fills it with the inherited colour, so
   * one monochrome file serves a near-black marketing canvas and a light app
   * shell. `own` paints the file as it is.
   *
   * Not a style preference. e&'s mark is a single path filled #F20519, and that
   * red is the brand — masking it would render e& in whatever colour the header
   * happens to inherit, which is a different company's logo in all but outline.
   * Unicity's asset is monochrome and genuinely needs the mask.
   */
  tint: 'currentColor' | 'own';
  /**
   * The asset's intrinsic ratio, `w / h`, so the mark cannot be distorted by
   * the width it is given. Taken from the file's own viewBox — asserted
   * against it in `platform-brand.test.ts`, because a guess here is invisible
   * until somebody looks at the logo: this was `641 / 128` for e& for one
   * commit, which would have drawn a near-square mark as a 5:1 smear.
   */
  ratio: string;
  /**
   * The typographic fallback, used when `wordmark` is null.
   *
   * Deliberately the brand's NAME set in type, never an approximation of its
   * logo. A hand-drawn lookalike of somebody else's trademark is worse than
   * plain text in every direction: it is wrong, it is theirs, and it reads as
   * finished so nobody replaces it.
   */
  text: string;
};

const PLATFORM_BRANDS = {
  unicity: {
    id: 'unicity',
    label: 'Unicity Labs',
    wordmark: '/images/unicity-logo.svg',
    tint: 'currentColor',
    /* 641×128 ≈ 5:1, the asset's own viewBox. */
    ratio: '641 / 128',
    text: 'Unicity',
  },
  /**
   * e& — the bid this estate is built for.
   *
   * The official mark, from the pitch deck's telco set: one path, filled
   * #F20519, viewBox 530.26 × 493.52. Painted in its own colour rather than
   * masked, for the reason `tint` gives.
   *
   * `text` stays as the fallback the component uses if the file ever goes
   * missing — the NAME, never an approximation of the wordmark.
   */
  eand: {
    id: 'eand',
    label: 'e&',
    wordmark: '/images/eand-logo.svg',
    tint: 'own',
    ratio: '530.26 / 493.52',
    text: 'e&',
  },
} as const satisfies Record<string, PlatformBrand>;

export type PlatformBrandId = keyof typeof PLATFORM_BRANDS;

/**
 * Read once, at module load, and refused loudly if it names nothing.
 *
 * `NEXT_PUBLIC_` so the value is available to client components without a round
 * trip; it is a brand name on a header, not a secret. Changing it is a restart,
 * which is what "switch at demo time" needs to mean for a build-time constant —
 * the alternative is reading it per request on every surface, and a logo that
 * can change between two renders of the same page is a bug, not a feature.
 */
const PlatformBrandSchema = z
  .enum(
    Object.keys(PLATFORM_BRANDS) as [PlatformBrandId, ...PlatformBrandId[]],
    {
      error: `NEXT_PUBLIC_PLATFORM_BRAND must name one of: ${Object.keys(PLATFORM_BRANDS).join(', ')}`,
    },
  )
  .default('unicity');

export const platformBrand: PlatformBrand =
  PLATFORM_BRANDS[
    PlatformBrandSchema.parse(
      process.env.NEXT_PUBLIC_PLATFORM_BRAND || undefined,
    )
  ];

export { PLATFORM_BRANDS };
